import { TimeIcon, WarningIcon } from '@chakra-ui/icons';
import {
  IDIncomeVerificationStatus,
  SystemTranslationKeys,
  VerificationType,
} from '@common/entities';
import { Dispatch } from 'redux';
import { setIDVerification, setIncomeVerification } from 'state/slices/idIncomeVerificationSlice';

import { CircleCheckIcon } from 'components/custom-icons';

const STATUS = {
  [IDIncomeVerificationStatus.NotStarted]: {
    icon: TimeIcon,
    color: 'blue.600',
    labelKey: SystemTranslationKeys.summary.notStarted,
    bg: 'blue.200',
  },
  [IDIncomeVerificationStatus.InProgress]: {
    icon: TimeIcon,
    color: 'blue.600',
    labelKey: SystemTranslationKeys.summary.inProgress,
    bg: 'blue.200',
  },
  [IDIncomeVerificationStatus.Success]: {
    icon: CircleCheckIcon,
    color: 'green.600',
    bg: 'green.200',
    labelKey: SystemTranslationKeys.summary.success,
  },
  [IDIncomeVerificationStatus.Failed]: {
    icon: WarningIcon,
    color: 'yellow.600',
    bg: 'yellow.200',
    labelKey: SystemTranslationKeys.summary.failed,
  },
};
const VERIFICATION_STATUSES = new Set([
  IDIncomeVerificationStatus.InProgress,
  IDIncomeVerificationStatus.Success,
  IDIncomeVerificationStatus.Failed,
  IDIncomeVerificationStatus.NotStarted,
]);

/**
 * Configuration for verification status update
 */
interface VerificationStatusConfig {
  newStatus: IDIncomeVerificationStatus;
  currentStatus: IDIncomeVerificationStatus | null;
  verificationType: VerificationType;
  fieldPageIndex: number;
  dispatch: Dispatch;
  handleVerificationStatus: (
    status: IDIncomeVerificationStatus,
    type: VerificationType,
    pageIndex: number,
  ) => void;
}

/**
 * Result of processing verification status update
 */
interface VerificationStatusResult {
  statusChanged: boolean;
  isInProgress: boolean;
}

/**
 * Handles verification status update following DRY principles
 * - Checks if status has changed
 * - Dispatches Redux action to update state
 * - Calls handler only if status changed
 * - Returns whether status is in progress
 */
export function processVerificationStatusUpdate(
  config: VerificationStatusConfig,
): VerificationStatusResult {
  const {
    newStatus,
    currentStatus,
    verificationType,
    fieldPageIndex,
    dispatch,
    handleVerificationStatus,
  } = config;

  const statusChanged = newStatus !== currentStatus;

  // Dispatch appropriate action based on verification type
  if (verificationType === VerificationType.IdentityVerification) {
    dispatch(setIDVerification({ status: newStatus }));
  } else {
    dispatch(setIncomeVerification({ status: newStatus }));
  }

  // Only call handler if status has changed
  if (statusChanged) {
    handleVerificationStatus(newStatus, verificationType, fieldPageIndex);
  }

  const isInProgress = newStatus === IDIncomeVerificationStatus.InProgress;

  return { statusChanged, isInProgress };
}

export { STATUS, VERIFICATION_STATUSES };
